<template>
  <div class="flex-grow-1">
    <router-view v-if="appbarVisibility" name="appbar" />
    <AppBarExt v-else-if="isExtensionPopup" />

    <router-view v-if="footerVisibility" v-slot="{ Component }" name="footer">
        <transition name="fade-transition">
          <component :is="Component" />
        </transition>
      </router-view>

    <AppBottomNav v-if="isMobile && (!isPublicRoute || isLoggedIn)" :order="appBottomNavOrder" />

    <router-view v-if="sidebarVisibility" v-slot="{ Component }" name="sidebar">
      <transition name="fade-transition" appear mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>


    <v-main class="full-height" :class="{'page-transitioning': transitioning}">
      <!-- guest account alert -->
      <v-alert
        v-model="showGuestAccountNotice"
        color="orange25 text-orange900"
        class="guest-alert text-body-2 text-md-center"
        closable
      >
        <span class="d-inline-flex">
          <PhInfo class="text-h5 text-sm-h6 flex-shrink-0 mr-2 text-orange400" weight="fill"  />
          <span>
            {{ $t('alert.guest_account.text') }}

            <router-link class="text-orange600 font-weight-semibold" to="/auth/signup?guest=true&autoLogin=false">
              {{ $t('alert.guest_account.cta') }}
            </router-link>
          </span>
        </span>
        <template #close>
          <BrandButton size="small" icon @click="showGuestAccountNotice = false">
            <PhX class="text-h6" />
          </BrandButton>
        </template>
      </v-alert>

      <!-- prompt web desktop users without the extension to install it -->
      <ExtensionInstallBanner />

      <router-view v-slot="{ Component, route: viewRoute }" class="route-page flex-grow-1">
        <transition
          :name="transitionName"
          mode="out-in"
          @before-leave="onBeforeLeave"
          @after-leave="onAfterLeave"
          @after-enter="onAfterEnter"
        >
          <component :is="Component" :key="viewRoute.path" />
        </transition>
      </router-view>


    </v-main>

  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { PhInfo, PhX } from '@phosphor-icons/vue'
import AppBottomNav from '@/components/AppBottomNav.vue'
import AppBarExt from '@/components/appbars/AppBarExt.vue'
import ExtensionInstallBanner from '@/components/ExtensionInstallBanner.vue'
import { getRouteInheritedMeta } from '@/utils/helpers'
import { useRouteTransition } from '@/composables/useRouteTransition'
import { useDeviceAware } from '@/composables/useDeviceAware'
import { useDisplay } from 'vuetify'

// Get route and store
const route = useRoute()
const store = useStore()
const display = useDisplay()

// Use route transition and device aware composables
const {
  transitionName,
  transitioning,
  onBeforeLeave,
  onAfterLeave,
  onAfterEnter,
} = useRouteTransition()
const {
  isExtension,
  isExtensionPopup,
  isDesktop,
  isWebApp,
  isMobile,
  isNative
} = useDeviceAware()


const showGuestAccountNotice = computed({
  get: () => store.getters['auth/showGuestAccountNotice'],
  set: (value) => {
    // When dismissed (set to false), mark it as dismissed in the store
    if (!value) {
      store.commit('auth/setGuestAccountNoticeDismissed', true)
    }
  }
})


// Computed properties for visibility
const appbarVisibility = computed(() => {
  const meta = getRouteInheritedMeta(route)

  if (typeof meta.appbarVisibility === 'function') {
    return meta.appbarVisibility({
      isExtension: isExtension.value,
      isExtensionPopup: isExtensionPopup.value,
      isDesktop: isDesktop.value,
      isWebApp: isWebApp.value,
      isMobile: isMobile.value,
      isNative: isNative.value
    })
  }

  return meta.appbarVisibility ?? (isDesktop.value && !isExtensionPopup.value)
})

const sidebarVisibility = computed(() => {
  const meta = getRouteInheritedMeta(route)

  if (typeof meta.sidebarVisibility === 'function') {
    return meta.sidebarVisibility({
      isExtension: isExtension.value,
      isExtensionPopup: isExtensionPopup.value,
      isDesktop: isDesktop.value,
      isWebApp: isWebApp.value,
      isMobile: isMobile.value,
      isNative: isNative.value,
      display: display
    })
  }

  return meta.sidebarVisibility ?? (isDesktop.value && !isExtensionPopup.value)
})

const footerVisibility = computed(() => {
  const meta = getRouteInheritedMeta(route)

  if (typeof meta.footerVisibility === 'function') {
    return meta.footerVisibility({
      isExtension: isExtension.value,
      isExtensionPopup: isExtensionPopup.value,
      isDesktop: isDesktop.value,
      isWebApp: isWebApp.value,
      isMobile: isMobile.value,
      isNative: isNative.value
    })
  }

  return meta.footerVisibility ?? (isDesktop.value && !isExtensionPopup.value)
})

const isPublicRoute = computed(() => {
  const meta = getRouteInheritedMeta(route)
  return !!meta.isPublicRoute
})

const isLoggedIn = computed(() => !!store.getters['auth/userId'])

const appBottomNavOrder = computed(() => {
  const meta = getRouteInheritedMeta(route)
  return meta.appBottomNav?.order ?? 2000
})
</script>

<style lang="scss" scoped>
.v-bottom-navigation span.text-caption {
  font-size: 10px !important;
}

// .page-transitioning {
//   overflow: hidden;
//   height: 100vh;
// }

.v-main :deep(.v-main__wrap) {
  display: flex;
  flex-direction: column;
}

.guest-alert :deep(.v-alert__wrapper) {
  align-items: center;
}
</style>
